import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle, Star, Users, Award, Clock, Target, Code, Smartphone } from 'lucide-react';

const ServiceDetail: React.FC = () => {
  const { service } = useParams<{ service: string }>();

  const serviceData = {
    'digital-marketing': {
      icon: Target,
      title: 'Digital Marketing',
      subtitle: 'Strategic campaigns that elevate your brand presence',
      description: 'Our digital marketing services are designed specifically for luxury brands who demand excellence. We create comprehensive strategies that not only increase visibility but also maintain the exclusivity and premium positioning that defines luxury brands.',
      features: [
        'Search Engine Optimization (SEO)',
        'Pay-Per-Click Advertising (PPC)',
        'Social Media Marketing',
        'Content Marketing Strategy',
        'Email Marketing Campaigns',
        'Brand Positioning & Messaging',
        'Influencer Partnerships',
        'Analytics & Performance Tracking'
      ],
      process: [
        {
          step: '01',
          title: 'Brand Analysis',
          description: 'Deep dive into your brand positioning, target audience, and competitive landscape.'
        },
        {
          step: '02',
          title: 'Strategy Development',
          description: 'Create comprehensive digital marketing strategies tailored to your luxury brand.'
        },
        {
          step: '03',
          title: 'Campaign Execution',
          description: 'Implement and manage campaigns across all relevant digital channels.'
        },
        {
          step: '04',
          title: 'Optimization & Reporting',
          description: 'Continuous optimization and detailed reporting on campaign performance.'
        }
      ],
      pricing: {
        starter: '$5,000/month',
        professional: '$10,000/month',
        enterprise: 'Custom pricing'
      },
    },
    'web-development': {
      icon: Code,
      title: 'Web Development',
      subtitle: 'Bespoke websites that reflect your luxury brand',
      description: 'We create stunning, high-performance websites that perfectly capture your brand\'s essence. Our development process ensures every detail reflects the quality and sophistication your customers expect from a luxury brand.',
      features: [
        'Custom Website Development',
        'WordPress Solutions',
        'E-commerce Platforms',
        'API Development & Integration',
        'Performance Optimization',
        'Maintenance & Support'
      ],
      process: [
        {
          step: '01',
          title: 'Discovery & Planning',
          description: 'Understanding your brand, goals, and technical requirements.'
        },
        {
          step: '02',
          title: 'Design & Prototyping',
          description: 'Creating stunning visual designs and interactive prototypes.'
        },
        {
          step: '03',
          title: 'Development & Testing',
          description: 'Building your website with cutting-edge technology and rigorous testing.'
        },
        {
          step: '04',
          title: 'Launch & Optimization',
          description: 'Deploying your site and ongoing performance optimization.'
        }
      ],
      pricing: {
        starter: '$15,000',
        professional: '$35,000',
        enterprise: 'Custom pricing'
      },
    },
    'app-development': {
      icon: Smartphone,
      title: 'App Development',
      subtitle: 'Mobile experiences that delight your customers',
      description: 'We develop premium mobile applications that provide exceptional user experiences while maintaining the luxury feel your brand demands. Our apps are built for performance, security, and scalability.',
      features: [
        'React Native Development',
        'iOS App Development',
        'Android App Development',
        'Cross-Platform Solutions',
        'UI/UX Design',
        'Play Store & App Store Live',
        // 'Backend Development',
        // 'Quality Assurance & Testing'
      ],
      process: [
        {
          step: '01',
          title: 'Concept & Strategy',
          description: 'Defining app concept, user journey, and technical architecture.'
        },
        {
          step: '02',
          title: 'Design & Prototyping',
          description: 'Creating intuitive user interfaces and interactive prototypes.'
        },
        {
          step: '03',
          title: 'Development & Testing',
          description: 'Building your app with robust code and comprehensive testing.'
        },
        {
          step: '04',
          title: 'Launch & Support',
          description: 'App store deployment and ongoing maintenance and updates.'
        }
      ],
      pricing: {
        starter: '$25,000',
        professional: '$50,000',
        enterprise: 'Custom pricing'
      },
    }
  };

  const currentService = serviceData[service as keyof typeof serviceData];

  if (!currentService) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4">Service Not Found</h1>
          <Link to="/services" className="btn-primary">Back to Services</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <div className="w-20 h-20 bg-gradient-to-r from-primary-500 to-accent-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <currentService.icon className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-5xl md:text-6xl font-serif font-bold mb-6">
              {currentService.title}
            </h1>
            <p className="text-xl text-neutral-600 mb-8 leading-relaxed">
              {currentService.subtitle}
            </p>
            <p className="text-lg text-neutral-600 leading-relaxed">
              {currentService.description}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: false }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-serif font-bold mb-6">What's Included</h2>
            <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
              Our comprehensive service includes everything you need to succeed in the digital landscape.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {currentService.features.map((feature, index) => (
              <motion.div
                key={feature}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: false }}
                className="flex items-center space-x-4 p-4 rounded-lg hover:bg-neutral-50 transition-colors duration-300"
              >
                <CheckCircle className="w-6 h-6 text-primary-500 flex-shrink-0" />
                <span className="text-neutral-700">{feature}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 bg-neutral-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: false }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-serif font-bold mb-6">Our Process</h2>
            <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
              We follow a proven methodology to ensure exceptional results for your luxury brand.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {currentService.process.map((step, index) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: false }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-gradient-to-r from-primary-500 to-accent-500 rounded-full flex items-center justify-center mx-auto mb-6 text-white font-bold text-xl">
                  {step.step}
                </div>
                <h3 className="text-xl font-semibold mb-4">{step.title}</h3>
                <p className="text-neutral-600 leading-relaxed">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-24 bg-neutral-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: false }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-serif font-bold mb-6">Investment Options</h2>
            <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
              Choose the package that best fits your needs. All packages include our premium service guarantee.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {Object.entries(currentService.pricing).map(([tier, price], index) => (
              <motion.div
                key={tier}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: false }}
                className={`bg-white rounded-2xl p-8 shadow-lg ${
                  tier === 'professional' ? 'ring-2 ring-primary-500 scale-105' : ''
                }`}
              >
                <div className="text-center">
                  <h3 className="text-xl font-semibold mb-4 capitalize">{tier}</h3>
                  <div className="text-3xl font-bold text-primary-600 mb-6">{price}</div>
                  <Link
                    to="/contact"
                    className={`w-full py-3 rounded-lg font-semibold transition-colors duration-300 ${
                      tier === 'professional'
                        ? 'bg-primary-600 text-white hover:bg-primary-700'
                        : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                    }`}
                  >
                    Get Started
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-primary-500 to-accent-500">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: false }}
          >
            <h2 className="text-4xl font-serif font-bold text-white mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Let's discuss how our {currentService.title.toLowerCase()} services can elevate your luxury brand.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="bg-white text-primary-600 px-8 py-4 rounded-lg font-semibold hover:bg-accent-50 transition-colors duration-300 inline-flex items-center justify-center">
                Start Your Project
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
              <Link to="/services" className="bg-white/20 backdrop-blur-sm text-white px-8 py-4 rounded-lg font-semibold hover:bg-white/30 transition-colors duration-300 inline-flex items-center justify-center">
                View All Services
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetail;
